/**
 * HARGA PERKIRAAN (mig 6840) — helper MURNI.
 *
 * Produk PO "harga menyusul" boleh membawa SATU angka perkiraan
 * (`products.price_estimate`) yang tampil di halaman produk sebagai
 * "Perkiraan Rp X" dan dikirim ke Katalog Instagram — Meta mewajibkan `price`
 * di setiap item, jadi tanpa angka ini produknya tak pernah masuk katalog.
 * Keputusan pemilik 2026-10-01 ("Lanjut" atas rencana opsi 3): hanya produk
 * PO, satu angka per produk, hanya halaman produk (kartu beranda tetap tanpa
 * baris harga), JSON-LD/Google TIDAK diberi angka ini.
 *
 * PAJANGAN SAJA. Jalur uang — `pricing.ts`, checkout, nota, DP, laporan,
 * ekspor — tak pernah membacanya: pesanan produk berperkiraan tetap
 * `order_items.unit_price` NULL ("harga menyusul"). Dikunci
 * `src/lib/__tests__/priceEstimateLint.test.ts`.
 *
 * BERLAKU hanya bila (cermin SQL mig 6840 §3–§4):
 *   • produk preorder;
 *   • perkiraan terisi & > 0;
 *   • produk TANPA satu pun harga asli: `price_normal` produk kosong DAN tak
 *     ada varian ber-`price_normal`. Mode malas (`variations` sengaja kosong)
 *     membaca ringkasan agregat SQL: `variantPriceRange === null` = memang tak
 *     berharga; tak diketahui (`variantPriceUncertain`) → TIDAK berlaku
 *     (fail-closed: lebih baik "Harga menyusul" daripada angka di samping
 *     harga asli yang tak terlihat).
 * Satu harga asli → perkiraan diabaikan di mana pun.
 *
 * PER VARIAN (putusan pemilik 2026-10-21, bughunt D15-29): perkiraan varian =
 * perkiraan produk + tambahan varian itu (`variantPriceEstimate`).
 */

import { formatPrice, variantPriceExtra } from "@/lib/pricing";
import type { Product, ProductVariation } from "@/lib/products";

export type PriceEstimateInput = Pick<
  Product,
  | "productType"
  | "price_normal"
  | "priceEstimate"
  | "variations"
  | "variantsLazy"
  | "variantPriceRange"
  | "variantPriceUncertain"
>;

/** Nilai mentah DB (numeric bisa tiba sebagai angka atau teks) → angka > 0 atau undefined. */
export function parsePriceEstimate(raw: unknown): number | undefined {
  const value = typeof raw === "string" && raw.trim() !== "" ? Number(raw) : raw;
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Produk ini punya satu pun harga asli? (lihat aturan di kepala berkas) */
export function hasAnyRealPrice(product: PriceEstimateInput): boolean | "unknown" {
  if (product.price_normal !== undefined && product.price_normal !== null) return true;
  if (product.variantsLazy) {
    if (product.variantPriceUncertain) return "unknown";
    // `null` = agregat SQL memastikan tak ada varian berharga; `undefined` =
    // ringkasan tak ikut terbawa → tak diketahui.
    if (product.variantPriceRange === null) return false;
    return product.variantPriceRange === undefined ? "unknown" : true;
  }
  return product.variations.some(
    (v) => v.price_normal !== undefined && v.price_normal !== null
  );
}

/** Angka perkiraan yang BERLAKU untuk produk ini, atau undefined. */
export function effectivePriceEstimate(product: PriceEstimateInput): number | undefined {
  if ((product.productType ?? "preorder") !== "preorder") return undefined;
  const estimate = parsePriceEstimate(product.priceEstimate);
  if (estimate === undefined) return undefined;
  return hasAnyRealPrice(product) === false ? estimate : undefined;
}

/**
 * Perkiraan SATU varian = perkiraan produk + tambahan varian itu (mig 7900;
 * bughunt D15-29). Tambahan ditempel sesudah harga dasar ditetapkan, jadi XXL
 * +Rp 20.000 berperkiraan Rp 195.000 — bukan Rp 175.000 yang kelak kurang.
 * Varian tak diketahui (belum dipilih; kombinasi malas belum bermateri, yang
 * barunya mewarisi tambahan 0 karena tak ada saudara berharga) = perkiraan
 * produk. Angka yang SAMA untuk halaman produk dan item feed Instagram.
 */
export function variantPriceEstimate(
  estimate: number,
  variation: Pick<ProductVariation, "price_extra"> | null | undefined
): number {
  return estimate + variantPriceExtra(variation);
}

/** "Perkiraan Rp 175.000" — baris harga halaman produk. */
export function priceEstimateLabel(amount: number): string {
  return `Perkiraan ${formatPrice(amount)}`;
}

/** Catatan di bawah angka perkiraan — menggantikan "Harga menyusul — …". */
export const PRICE_ESTIMATE_NOTE =
  "Harga final dikonfirmasi admin sebelum pembayaran — pesanan tetap bisa dibuat sekarang.";

/** Kalimat pembuka deskripsi item Katalog Instagram. */
export function metaPriceEstimateSentence(amount: number): string {
  return `Harga perkiraan ${formatPrice(amount)}, harga final dikonfirmasi sebelum pembayaran.`;
}

// ─── Form produk ────────────────────────────────────────────────────────────

/**
 * Isian form "Harga Perkiraan" → angka rupiah utuh, `null` (kosong = tanpa
 * perkiraan), atau pesan galat. Angka ≤ 0 ditolak di sini DAN oleh CHECK DB
 * (`products_price_estimate_positive`, mig 6840).
 */
export function parsePriceEstimateInput(
  text: string
): { ok: true; value: number | null } | { ok: false; message: string } {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || Math.round(value) <= 0) {
    return { ok: false, message: "Harga Perkiraan harus angka rupiah lebih dari 0 (atau kosongkan)." };
  }
  return { ok: true, value: Math.round(value) };
}

/**
 * Nilai `products.price_estimate` yang DITULIS form produk. Hanya PO yang tak
 * punya satu pun harga asli yang menyimpan angka; selain itu NULL — angka lama
 * tak boleh muncul kembali diam-diam bila harga asli kelak dihapus atau produk
 * kembali menjadi PO.
 */
export function priceEstimateForSave(input: {
  productType: "preorder" | "ready_stock";
  /** Harga produk hasil simpan (`productPriceFromVariants`); null = harga menyusul. */
  productPriceNormal: number | null;
  /** Hasil `parsePriceEstimateInput` yang sudah lolos. */
  value: number | null;
}): number | null {
  if (input.productType !== "preorder") return null;
  if (input.productPriceNormal !== null) return null;
  return input.value;
}
